// Parses the partner bank's invoice sheet ("ALM Outsourcing FTM <month>.xlsx"):
// id | date | person_name | cnic | AccountNumber | BankName | phoneNo | Amount |
// Rate | Per Annum Rate | Perday Markup | Tenure ... | Prorate Markup ... |
// Total Invoiced Amount ... | Repayment Date
import * as XLSX from "xlsx";

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

const text = (value) => String(value ?? "").trim();
const norm = (value) => text(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const pad = (n) => String(n).padStart(2, "0");
const fullYear = (y) => (y < 100 ? 2000 + y : y);

export function parseSheetDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    // SheetJS returns date cells shifted by the local offset; +12h puts the
    // UTC calendar day back on the day that was typed.
    const shifted = new Date(value.getTime() + 12 * 3600 * 1000);
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
  }
  const raw = text(value);
  let match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/); // m/d/yy as the bank exports it
  if (match) return `${fullYear(Number(match[3]))}-${pad(match[1])}-${pad(match[2])}`;
  match = raw.match(/^(\d{1,2})[- ]([A-Za-z]{3})[a-z]*[- ,]+(\d{2,4})$/); // 9-Oct-26
  if (match && MONTHS[match[2].toLowerCase()]) return `${fullYear(Number(match[3]))}-${pad(MONTHS[match[2].toLowerCase()])}-${pad(match[1])}`;
  return "";
}

const num = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, "").replace(/%/g, "").trim());
  return Number.isFinite(parsed) ? parsed : NaN;
};

function column(headers, ...options) {
  return headers.findIndex((header) => options.some((option) => header === option || header.startsWith(option)));
}

export function parseBankSheet(buffer) {
  const workbook = XLSX.read(buffer, { cellDates: true });
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: "", raw: true });
  const headerIndex = rows.findIndex((row) => {
    const headers = row.map(norm);
    return headers.includes("id") && headers.includes("amount") && headers.some((h) => h.startsWith("person"));
  });
  if (headerIndex < 0) throw new Error("This does not look like the bank's invoice sheet — no id / person_name / Amount header row found.");
  const headers = rows[headerIndex].map(norm);
  const at = {
    id: column(headers, "id"), date: column(headers, "date"), name: column(headers, "person"), cnic: column(headers, "cnic"),
    account: column(headers, "accountnumber", "account number"), bank: column(headers, "bankname", "bank name"), phone: column(headers, "phoneno", "phone"),
    amount: column(headers, "amount"), rate: column(headers, "rate"), perDay: column(headers, "perday"),
    days: column(headers, "tenure"), markup: column(headers, "prorate"), total: column(headers, "total invoiced"),
    repayment: column(headers, "repayment date")
  };
  if (at.id < 0 || at.date < 0 || at.name < 0 || at.amount < 0) throw new Error("The bank sheet is missing the id, date, person_name or Amount column.");

  const parsed = [];
  const errors = [];
  rows.slice(headerIndex + 1).forEach((row, offset) => {
    const excelRow = headerIndex + offset + 2;
    const id = text(row[at.id]);
    if (!id) return; // totals / blank rows
    const date = parseSheetDate(row[at.date]);
    const amount = num(row[at.amount]);
    const name = text(row[at.name]);
    if (!date) { errors.push(`Row ${excelRow}: the withdrawal date "${text(row[at.date])}" could not be read.`); return; }
    if (!(amount > 0)) { errors.push(`Row ${excelRow}: the amount "${text(row[at.amount])}" is not valid.`); return; }
    if (!name) { errors.push(`Row ${excelRow}: person_name is empty.`); return; }
    const rate = at.rate >= 0 ? num(row[at.rate]) : NaN;
    const repaymentDate = at.repayment >= 0 ? parseSheetDate(row[at.repayment]) : "";
    parsed.push({
      bankTxnId: id,
      date,
      employeeName: name,
      employeeCnic: text(row[at.cnic]),
      employeeAccount: text(row[at.account]),
      employeeBank: text(row[at.bank]),
      employeePhone: text(row[at.phone]),
      amount,
      // "1.90%" arrives as the text 1.90% or as the number 0.019, depending on cell format.
      monthlyRate: Number.isFinite(rate) ? (rate > 1 ? rate / 100 : (typeof row[at.rate] === "number" ? rate : rate / 100)) : null,
      bankInvoice: repaymentDate ? {
        repaymentDate,
        days: num(row[at.days]),
        markup: num(row[at.markup]),
        total: num(row[at.total]),
        perDay: num(row[at.perDay])
      } : null,
      sheetRow: excelRow
    });
  });
  return { rows: parsed, errors };
}
